import { Box, Icon, Modal, Typography, CircularProgress, Tooltip } from '@mui/material'
import React, { useRef, useState } from 'react'
import AdxDropzoneMultiple from 'components/reusable/AdxDropzoneMultiple'
import { parseResume } from 'services/api/modules/recruitment/resume-parser'
import { useMutation } from 'react-query'
import useOpenClose from 'services/hooks/useOpenClose'
import UploadHistoryDrawer from './UploadHistoryDrawer'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxButton from 'components/custom/AdxButton'

const ImportTalentModal = ({ isParsing, refreshQueries, progressBarData, remainingQuota }) => {
  const dropzoneRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [files, setFiles] = useState([])
  const maxFileSize = 6291456
  const maxTotalAllowedFiles = 10

  const totalFileSizeError = () => {
    let total = 0
    files.forEach((item) => {
      total += item.size
    })
    return total > maxFileSize
  }

  const fileError =
    files?.find((i) => i.errors && i.errors.some((error) => error.code === 'file-too-large')) ||
    files.length > maxTotalAllowedFiles ||
    totalFileSizeError()

  const parsingModalState = useOpenClose(false)
  const [drawerState, setDrawerState] = useState(false)

  const { mutate, isLoading } = useMutation((data) => parseResume(data), {
    onSuccess: () => {
      setSuccessModalOpen(true)
    },
    onError: (err) => {
      console.error(err)
    },
  })

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
    setFiles([])
  }

  const handleDrop = (acceptedFiles, fileRejections) => {
    setFiles((prev) => [...prev, ...acceptedFiles, ...fileRejections])
  }

  const handleRemoveFile = (index) => {
    const updatedFiles = [...files]
    updatedFiles.splice(index, 1)
    setFiles(updatedFiles)
  }

  const handleUpload = () => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    mutate(formData)
    handleToggle()
  }

  const handleSuccessUpload = () => {
    refreshQueries()
    setSuccessModalOpen(false)
  }

  const openDropzone = () => {
    if (dropzoneRef.current && dropzoneRef.current.open) {
      dropzoneRef.current.open()
    }
  }

  const handleDrawer = (anchor, open) => () => {
    parsingModalState.handleClose()
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  return (
    <Box>
      {isLoading && (
        <Box
          sx={{
            height: '100vh',
            width: '100vw',
            backgroundColor: 'rgba(4, 4, 4, 0.55)',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <CircularProgress
            color="white"
            size={45}
            sx={{ boxShadow: 'inset 0 0 0 4px #09A3A9', borderRadius: '100%' }}
          />
          <Typography color="white.main">Uploading your file..</Typography>
        </Box>
      )}
      {progressBarData === 100 ? (
        <Tooltip title="You're out of quota">
          <Box>
            <AdxButton
              variant={'contained'}
              sx={{ width: '150px' }}
              onClick={isParsing ? parsingModalState.handleOpen : handleToggle}
              disabled={true}
            >
              Import talent
            </AdxButton>
          </Box>
        </Tooltip>
      ) : (
        <AdxButton
          variant={'contained'}
          sx={{ width: '150px' }}
          onClick={isParsing ? parsingModalState.handleOpen : handleToggle}
        >
          Import talent
        </AdxButton>
      )}
      <AdxFormModal
        open={open}
        handleClose={handleToggle}
        title={'Import External Talent'}
        icon={<Icon>download</Icon>}
        enableFooter={files.length !== 0}
        actionText="Confirm Upload"
        handleAction={handleUpload}
        cancelText="Cancel"
        actionButtonDisabled={fileError || files.find((i) => i.errors) !== undefined}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ marginBottom: '16px' }}>
              Upload candidate resumes by selecting one or multiple files. Drag and drop or select
              the resumes to upload.
            </Typography>
            <AdxDropzoneMultiple
              mainText="Click or drag resume here"
              optionalText="(Max 6mb, format .pdf)"
              sx={{ height: '250px' }}
              useDropzoneObject={{
                accept: ['.pdf'],
                maxSize: maxFileSize,
                maxFiles: 200,
                onDrop: handleDrop,
              }}
              ref={dropzoneRef}
            />
          </Box>
          <Box sx={{ display: files.length === 0 ? 'none' : '' }}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: 'default-background.main',
                  borderRadius: '4.5px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon sx={{ color: fileError ? 'error.main' : '#2FED64', fontSize: '20px' }}>
                    {fileError ? 'error' : 'check_circle'}
                  </Icon>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {fileError
                      ? "You've reached the upload limit of 6MB or 10 files. Please remove some of your files."
                      : files.length > 1
                      ? `${files.length} files selected`
                      : `${files.length} file selected`}
                  </Typography>
                </Box>
                {!fileError && (
                  <Typography
                    onClick={openDropzone}
                    sx={{
                      color: 'alternate.main',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}
                  >
                    <Icon sx={{ fontSize: '20px' }}>add</Icon> Add more files
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  borderRadius: '4.5px',
                  border: '1px solid #E5E5E5',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '50vh',
                  overflowY: 'auto',
                }}
              >
                {files.map((file, index) => {
                  const fileWithError = files.find(
                    (i) =>
                      i.errors &&
                      i.errors.some(
                        (error) =>
                          error.code === 'file-too-large' || error.code === 'file-invalid-type'
                      )
                  )
                  const error = fileWithError?.errors?.find(
                    (error) => error.code === 'file-too-large' || error.code === 'file-invalid-type'
                  )

                  return (
                    <Box
                      key={index}
                      sx={{
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTop: index > 0 ? '1px solid #E5E5E5' : 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Box
                          sx={{
                            width: '35px',
                            height: '35px',
                            borderRadius: '100%',
                            backgroundColor: file.file ? 'white.main' : 'default-background.main',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Icon
                            sx={{
                              color: file.file ? 'error.main' : 'alternate.main',
                              fontSize: '20px',
                            }}
                          >
                            {file.file ? 'error' : 'description'}
                          </Icon>
                        </Box>
                        {file.file ? (
                          <Box>
                            <Typography>{file.file.name}</Typography>
                            {error && error.code === 'file-too-large' && (
                              <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
                                File size is too large
                              </Typography>
                            )}
                            {error && error.code === 'file-invalid-type' && (
                              <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
                                File must be in pdf format
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Typography>{file.name}</Typography>
                            <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main' }}>
                              {(file.size / 1024).toFixed(1)} kb
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Icon
                        onClick={() => handleRemoveFile(index)}
                        sx={{ color: 'error.main', cursor: 'pointer' }}
                      >
                        close
                      </Icon>
                    </Box>
                  )
                })}
              </Box>
            </Box>
            {/* <Box
            sx={{
              padding: '24px 24px 32px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <AdxButton
              onClick={handleToggle}
              color="error"
              sx={{ width: '150px', '&:hover': { backgroundColor: 'error' } }}
            >
              Cancel
            </AdxButton>
            <AdxButton
              onClick={handleUpload}
              disabled={fileError || files.find((i) => i.errors) !== undefined}
              sx={{ width: '225px' }}
            >
              Confirm upload
            </AdxButton>
          </Box> */}
          </Box>
        </Box>
      </AdxFormModal>

      {/* Success modal */}
      <Modal
        open={successModalOpen}
        sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: 'white.main',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '60px 32px 32px 32px',
            borderRadius: '6.5px',
          }}
        >
          <Box
            sx={{
              width: '90px',
              height: '90px',
              backgroundColor: 'gray-background.main',
              borderRadius: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '36px', height: '36px', color: 'alternate.main' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                />
              </svg>
            </Box>
          </Box>
          <Typography variant="h4" sx={{ marginTop: '32px', marginBottom: '12px' }}>
            We're processing your resumes behind the scenes
          </Typography>
          <Typography color="light-gray-text.main" sx={{ marginBottom: '40px' }}>
            Resume your tasks as usual. Your talents will appear on the list once the parsing is
            done, and we'll notify you via email.
          </Typography>
          <AdxButton variant={'contained'} fullWidth onClick={handleSuccessUpload}>
            Got it
          </AdxButton>
        </Box>
      </Modal>

      {/* Parsing modal */}
      <Modal
        open={parsingModalState.isOpen}
        onClose={parsingModalState.handleClose}
        sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: 'white.main',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px',
            borderRadius: '6.5px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '24px',
            }}
          >
            <Icon onClick={parsingModalState.handleClose} sx={{ cursor: 'pointer' }}>
              close
            </Icon>
          </Box>
          <Box
            sx={{
              color: 'alternate.main',
              backgroundColor: 'default-background.main',
              width: '90px',
              height: '90px',
              borderRadius: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '32px',
            }}
          >
            <Box sx={{ width: '36px', height: '36px' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                />
              </svg>
            </Box>
          </Box>
          <Typography variant="h4" sx={{ marginBottom: '12px' }}>
            You’ve ongoing resume parsing in progress
          </Typography>
          <Typography sx={{ marginBottom: '40px' }}>
            Uploading new resume is disabled at the moment. You can add later once the ongoing
            parsing is completed.
          </Typography>
          <AdxButton variant={'outlined'} onClick={handleDrawer('right', true)} fullWidth>
            View progress
          </AdxButton>
        </Box>
      </Modal>

      {/* Upload history drawer */}
      <UploadHistoryDrawer drawerState={drawerState} handleDrawer={handleDrawer} />
    </Box>
  )
}

export default ImportTalentModal
